
import React from 'react';
import { Clock, Loader, CheckCircle, AlertCircle } from 'lucide-react';
import { compressorText, Locale } from '@/i18n/compressor';

interface StatusBadgeProps {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  retryCount: number;
  maxRetryAttempts: number;
  isPremium?: boolean;
  locale?: Locale;
}

const StatusBadge = ({ status, retryCount, maxRetryAttempts, isPremium = false, locale = 'es' }: StatusBadgeProps) => {
  const t = compressorText[locale].statusBadge;
  const getStatusIcon = () => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'processing':
        return <Loader className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'pending':
        return t.queued;
      case 'processing':
        return t.processing;
      case 'completed':
        return t.completed;
      case 'failed':
        return t.error(retryCount, maxRetryAttempts);
      default:
        return t.unknown;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'processing':
        return 'bg-blue-100 text-blue-700';
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'failed':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${getStatusColor()}`}>
      {getStatusIcon()}
      {getStatusText()}
    </div>
  );
};

export default StatusBadge;
